/**
 * 主軸カテゴリー管理タブ。商談ボードページ内のサブタブ。
 * v3.1.3
 *
 * UI: イベント管理画面と統一したカード形式リスト
 */
import { Edit, Plus, Tag, Trash2 } from 'lucide-react';
import { useMemo, useState } from 'react';

import { EmptyState } from '@/components/common/EmptyState';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import { toast } from '@/components/ui/toast';
import { useDeals } from '@/hooks/useDeals';
import {
  useDeleteProductCategory,
  useProductCategories,
  type ProductCategoryRecord,
} from '@/hooks/useProductCategories';

import { ProductCategoryDialog } from './ProductCategoryDialog';

export function CategoryManagementTab() {
  const { data: categories, isLoading } = useProductCategories({ includeInactive: true });
  const { data: deals } = useDeals();
  const deleteMutation = useDeleteProductCategory();

  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<ProductCategoryRecord | null>(null);

  // 各カテゴリーが何件の商談から参照されているか
  const usageCount = useMemo(() => {
    const m = new Map<string, number>();
    for (const d of deals ?? []) {
      if (d.primaryCategoryId) {
        m.set(d.primaryCategoryId, (m.get(d.primaryCategoryId) ?? 0) + 1);
      }
    }
    return m;
  }, [deals]);

  const handleDelete = async (cat: ProductCategoryRecord) => {
    const used = usageCount.get(cat.id) ?? 0;
    const msg =
      used > 0
        ? `カテゴリー「${cat.name}」を削除します。${used} 件の商談が参照していますが、表示用の名前は残ります。よろしいですか？`
        : `カテゴリー「${cat.name}」を削除します。よろしいですか？`;
    if (!window.confirm(msg)) return;
    try {
      await deleteMutation.mutateAsync({ id: cat.id });
      toast.success('カテゴリーを削除しました');
    } catch (err) {
      toast.error(
        `削除失敗: ${err instanceof Error ? err.message : String(err)}`,
      );
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-xs text-[var(--text-muted)]">
          商談・フォームの主軸カテゴリーを管理します。
          名称を変更すると、関連する商談・フォームの表示名が自動更新されます。
        </p>
        <Button type="button" variant="primary" onClick={() => setCreating(true)}>
          <Plus />
          新規追加
        </Button>
      </div>

      {isLoading ? (
        <Spinner />
      ) : !categories || categories.length === 0 ? (
        <EmptyState
          icon={Tag}
          title="カテゴリーが登録されていません"
          description="「新規追加」から登録できます。例: プロ機器 / コンシューマ機器"
        />
      ) : (
        <div className="rounded bg-[var(--bg-surface)] border border-[var(--border)] overflow-hidden">
          {categories.map((cat) => {
            const used = usageCount.get(cat.id) ?? 0;
            return (
              <div
                key={cat.id}
                className="flex items-center gap-4 p-4 border-b border-[var(--border)] last:border-b-0 hover:bg-[var(--bg-hover)] transition-colors"
              >
                <Tag className="size-4 text-[var(--text-muted)] flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-semibold text-[13px] truncate">
                      {cat.name}
                    </span>
                    {!cat.isActive && <Badge variant="default">停止中</Badge>}
                    {used > 0 && (
                      <Badge variant="sent">商談 {used} 件</Badge>
                    )}
                  </div>
                  <div className="text-xs text-[var(--text-muted)] flex items-center gap-3 flex-wrap">
                    <span>表示順: {cat.displayOrder ?? '-'}</span>
                    {cat.description && <span>{cat.description}</span>}
                  </div>
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={() => setEditing(cat)}
                  title="編集"
                >
                  <Edit />
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={() => handleDelete(cat)}
                  title="削除"
                >
                  <Trash2 />
                </Button>
              </div>
            );
          })}
        </div>
      )}

      <ProductCategoryDialog
        open={creating || !!editing}
        existing={editing}
        onOpenChange={(o) => {
          if (!o) {
            setCreating(false);
            setEditing(null);
          }
        }}
      />
    </div>
  );
}
